import { ArrowLeft, Info } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { PermessiMatrice } from "@/components/admin/PermessiMatrice";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAdminUtentePermessi,
  usePutAdminUtentePermessi,
} from "@/hooks/useAdminPermessi";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";

const RUOLO_LABEL: Record<string, string> = {
  maitime_staff: "Maitime Staff",
  mp_presidenza: "Presidenza",
  mp_management: "Management",
  mp_planning: "Planning",
  mp_logistica: "Logistica",
  mp_commerciale: "Commerciale",
  mp_viewer: "Viewer",
};

export function AdminUtentePermessiPage() {
  const { email } = useParams<{ email: string }>();
  const { data: me, isLoading: loadingMe } = useMe();
  const decodedEmail = email ? decodeURIComponent(email) : "";
  const { data, isLoading } = useAdminUtentePermessi(decodedEmail);
  const mutation = usePutAdminUtentePermessi(decodedEmail);

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  if (!decodedEmail) {
    return <Navigate to="/admin/utenti" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin/utenti"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Utenti e accessi
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Permessi personali
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground font-mono">
          {decodedEmail}
        </h1>
        {data && (
          <div className="flex items-center gap-2 pt-1">
            <Badge variant="outline" className="text-[10px]">
              Ruolo: {RUOLO_LABEL[data.ruolo] ?? data.ruolo}
            </Badge>
            <Badge
              variant="outline"
              className={
                data.ruolo_configurato
                  ? "text-[10px] bg-success/10 text-success border-success/30"
                  : "text-[10px] bg-muted text-muted-foreground"
              }
            >
              {data.ruolo_configurato
                ? "Preset ruolo: configurato"
                : "Preset ruolo: default legacy"}
            </Badge>
          </div>
        )}
        <p className="text-sm text-muted-foreground max-w-2xl pt-2">
          Override personali sopra il preset del ruolo. <strong>Eredita</strong> =
          usa il valore del preset (o legacy). <strong>Sì/No</strong> = sovrascrive
          il preset per questo singolo utente.
        </p>
      </header>

      {isLoading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : data.utente_id === me?.id && data.override.length === 0 ? (
        <NotaUtente />
      ) : (
        <PermessiMatrice
          righeIniziali={data.override}
          presetRuolo={data.preset_ruolo}
          mode="override"
          onSave={(righe) => mutation.mutateAsync({ righe })}
          saving={mutation.isPending}
        />
      )}

      {data && data.utente_id !== me?.id && data.override.length === 0 && (
        <NotaUtente />
      )}

      {mutation.isError && (
        <p className="text-xs text-destructive">
          Errore salvataggio: {(mutation.error as Error).message}
        </p>
      )}
    </PageContainer>
  );
}

function NotaUtente() {
  return (
    <p className="flex items-start gap-2 text-xs text-muted-foreground rounded-md border bg-muted/30 px-3 py-2 max-w-2xl">
      <Info className="size-3.5 mt-0.5 shrink-0" />
      <span>
        Nessun override personale. L'utente usa il preset del suo ruolo. Per
        modificare i permessi di tutti gli utenti con questo ruolo, vai a{" "}
        <Link to="/admin/ruoli" className="underline">
          Ruoli e permessi
        </Link>
        .
      </span>
    </p>
  );
}
